<template>
    <v-dialog :modelValue="modelValue" @update:modelValue="syncVModel" :min-width="800" :width="1000">
        <v-card>
            <v-card-title>
                <h3>Detach Sensor</h3>
            </v-card-title>
            <v-card-text>
                <v-container>
                    <v-row>
                        <v-col md="4"> Detachment Reason: </v-col>
                        <v-col>
                            <v-btn-toggle v-model="reason" divided variant="outlined" color="primary" mandatory>
                                <v-btn value="accountMovement">Account Movement</v-btn>
                                <v-btn value="sensorFailure">Sensor Failure</v-btn>
                            </v-btn-toggle>
                        </v-col>
                    </v-row>
                    <SensorFailureForm v-if="reason === 'sensorFailure'" @form-state-update="formStateUpdate" />
                </v-container>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="gray-darken-1" @click="syncVModel(false)" size="large">Cancel</v-btn>
                <v-btn
                    v-if="reason === 'sensorFailure'"
                    color="red-darken-1"
                    @click="recordFailureAndDetachSensor"
                    :disabled="failureFormIsInvalid"
                    size="large"
                    >Record Failure and Detach</v-btn
                >
                <v-btn v-else color="red-darken-1" @click="detachMonitor" size="large">Detach</v-btn>
            </v-card-actions>
            <v-overlay contained :model-value="detachingSensor" class="align-center justify-center">
                <v-progress-circular indeterminate size="64"></v-progress-circular>
            </v-overlay>
        </v-card>
    </v-dialog>
</template>

<script lang="ts" setup>
import { useApi } from '@/api'
import { ref } from 'vue'
import { handleApiError } from '@/lib/utils'
import SensorFailureForm, { type FailureFormData, type FormStateUpdate } from '../sensors/SensorFailureForm.vue'

const reason = ref<'accountMovement' | 'sensorFailure'>('accountMovement')

const detachingSensor = ref(false)

const props = defineProps({
    monitorId: {
        type: String,
        required: true
    },
    serialNumber: {
        type: String,
        required: true
    },
    modelValue: {
        type: Boolean
    }
})

const emit = defineEmits<{
    (event: 'sensorDetached'): void
    (event: 'update:modelValue', value: boolean): void
}>()

const failureFormIsInvalid = ref(false)
const failureFormData = ref<FailureFormData | undefined>()

const formStateUpdate = (event: FormStateUpdate) => {
    failureFormIsInvalid.value = event.invalid
    failureFormData.value = event.state
}

const syncVModel = (value: boolean) => {
    emit('update:modelValue', value)
}

const detachMonitor = async () => {
    await handleApiError(
        async () => {
            detachingSensor.value = true
            const result = await useApi().deleteMonitorSensorV1({
                monitorId: props.monitorId
            })
            console.log('detached sensor', result)
            emit('sensorDetached')
            syncVModel(false)
        },
        async () => {
            detachingSensor.value = false
        }
    )
}

const recordFailureAndDetachSensor = async () => {
    if (failureFormIsInvalid.value === true) return

    const failureData = failureFormData.value
    if (failureData === undefined) return
    if (failureData.failureType === undefined) return

    await handleApiError(
        async () => {
            detachingSensor.value = true

            const result = await useApi().putSensorFailureV1({
                serialNumber: props.serialNumber,
                putSensorFailureBodyV1: {
                    ...failureData,
                    detachSensorFromMonitor: true
                }
            })

            console.log('reported failure and detached sensor', result)
            emit('sensorDetached')
            syncVModel(false)
        },
        async () => {
            detachingSensor.value = false
        }
    )
}
</script>

<style lang="scss" scoped></style>
